/**
 * Schaff Trend Cycle Tooltip Component
 *
 * Displays the current STC value and parameters.
 * Format: "STC (period,short,long,source): value"
 */

import React, { Component } from "react";
import PropTypes from "prop-types";
import displayValuesFor from "./displayValuesFor";
import GenericChartComponent from "../GenericChartComponent";
import { formatNumber, shouldHideField } from "../../../utils/numberFormatting";

import { isDefined, functor } from "../utils";
import { getSourcePathLabel } from "../utils/getSourcePath";
import ToolTipText from "./ToolTipText";
import ToolTipTSpanLabel from "./ToolTipTSpanLabel";

class SchaffTrendCycleTooltip extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
  }

  renderSVG(moreProps) {
    const {
      onClick,
      fontFamily,
      fontSize,
      yAccessor,
      displayFormat,
      className,
    } = this.props;
    const { options, labelFill, textFill } = this.props;
    const { displayValuesFor } = this.props;
    const {
      chartConfig: { width, height },
    } = moreProps;

    const currentItem = displayValuesFor(this.props, moreProps);
    const stc = isDefined(currentItem) && yAccessor(currentItem);
    const value = (stc && displayFormat(stc)) || "n/a";

    const { origin: originProp } = this.props;
    const origin = functor(originProp);
    const [x, y] = origin(width, height);

    // Hide tooltip if value is n/a
    if (shouldHideField(value)) {
      return null;
    }

    // Use displayName if provided, otherwise build from options
    let tooltipLabel;
    if (options.displayName) {
      // Use pre-computed displayName (handles series sources correctly)
      tooltipLabel = `${options.displayName}: `;
    } else {
      // Fallback: build from options
      const period = options.period || 10;
      const shortCycle = options.shortCycle || 23;
      const longCycle = options.longCycle || 50;
      const sourcePath = options.sourcePath || "close";
      const sourceLabel = getSourcePathLabel(sourcePath);
      tooltipLabel = `STC (${period},${shortCycle},${longCycle},${sourceLabel}): `;
    }

    return (
      <g
        className={className}
        transform={`translate(${x}, ${y})`}
        onClick={onClick}
        data-evo-id="tooltip-schaff-trend-cycle"
      >
        <ToolTipText x={0} y={0} fontFamily={fontFamily} fontSize={fontSize}>
          <ToolTipTSpanLabel fill={labelFill}>{tooltipLabel}</ToolTipTSpanLabel>
          <tspan fill={textFill}>{value}</tspan>
        </ToolTipText>
      </g>
    );
  }

  render() {
    return (
      <GenericChartComponent
        clip={false}
        svgDraw={this.renderSVG}
        drawOn={["mousemove"]}
      />
    );
  }
}

SchaffTrendCycleTooltip.propTypes = {
  origin: PropTypes.oneOfType([PropTypes.array, PropTypes.func]).isRequired,
  options: PropTypes.shape({
    period: PropTypes.number.isRequired,
    shortCycle: PropTypes.number.isRequired,
    longCycle: PropTypes.number.isRequired,
    sourcePath: PropTypes.string,
    displayName: PropTypes.string, // Pre-computed display name
  }).isRequired,
  className: PropTypes.string,
  fontFamily: PropTypes.string,
  fontSize: PropTypes.number,
  onClick: PropTypes.func,
  yAccessor: PropTypes.func.isRequired,
  displayFormat: PropTypes.func.isRequired,
  displayValuesFor: PropTypes.func,
  textFill: PropTypes.string,
  labelFill: PropTypes.string,
};

SchaffTrendCycleTooltip.defaultProps = {
  displayFormat: (d) => formatNumber(d, 2),
  displayValuesFor: displayValuesFor,
  origin: [0, 0],
  className: "react-stockcharts-tooltip",
};

export default SchaffTrendCycleTooltip;
